// Same-machine session sync between POS and CFD windows. ADDITIVE ONLY.
//
// The CFD ('/customer') needs the cashier's selected store + register +
// session token to call /v2/pos/bootstrap (which returns the company
// logo). zustand's `persist` middleware writes the session to
// localStorage but only re-hydrates on page load, so a CFD opened before
// POS logs in stays stuck on the welcome screen until refreshed.
//
// Design constraint: the CFD's sessionStore is UPGRADE-ONLY. Cross-window
// sync must never null/clear/downgrade the CFD's view, because the same
// store backs route guards (index/auth/login/select-* all redirect on
// missing token). Two consequences fall out of that:
//
//   1. POS only broadcasts when the snapshot is FULLY FORMED — token AND
//      selectedStore.id AND selectedRegister.id all set. Partial states
//      from the connect/subdomain/login/store-pick/register-pick cascade
//      are filtered at the publisher, never reach the wire.
//   2. There is no `session.cleared` event. POS logout doesn't broadcast.
//      The CFD's stale view is harmless — the cart channel already
//      broadcasts cart.cleared which flips the CFD to the welcome
//      screen; nothing on the CFD reads `sessionToken` to decide
//      navigation, so leaving it set is safe.
//
// Mirrors `cartChannel.ts` for transport selection (BroadcastChannel,
// localStorage fallback, noop) so behaviour matches the cart side and
// debugging tools work the same way.

import type { Register, Store } from '@/types'

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

/** The minimum slice the CFD needs to call /v2/pos/bootstrap. We
 *  deliberately omit fields that route guards key off (isAuthenticated,
 *  staff, authType, refreshToken, clerkToken) so a CFD setState applying
 *  this snapshot can't trigger a redirect as a side effect.
 *
 *  backendUrl IS included — without it the api client's baseURL is empty
 *  and v2() requests resolve against the dev server (404). Only the `/`
 *  index route reads backendUrl for redirect; `/customer` has no guard,
 *  so writing it on the CFD is safe. */
export interface SessionSnapshot {
  backendUrl: string
  sessionToken: string
  selectedStore: Store
  selectedRegister: Register
  /** Resolved display-logo URL from the auth response, or null when
   *  every server-side tier resolves empty. NOT part of the publish
   *  gate — null is a valid CFD value (falls back to the icon). Logo
   *  changes mid-session (e.g. clerk PIN upgrades store-tier → full
   *  hierarchy) re-broadcast via the fingerprint check below. */
  displayLogo: string | null
}

export interface SessionChannelMessage {
  type:
    | 'session.updated'         // POS → CFD: full session snapshot
    | 'session.request_state'   // CFD → POS: I just opened, send current state
    | 'session.bootstrap_invalidated'  // POS → CFD: clerk logged in, refetch bootstrap
  ts: number
  seq?: number
  session?: SessionSnapshot
}

// ---------------------------------------------------------------------------
// Transport: BroadcastChannel preferred, localStorage fallback.
// ---------------------------------------------------------------------------

const CHANNEL_NAME = 'waio.session'
const STORAGE_KEY = '__waio_session_channel__'

type Listener = (msg: SessionChannelMessage) => void

interface Transport {
  publish(msg: SessionChannelMessage): void
  subscribe(listener: Listener): () => void
  readonly active: boolean
  readonly kind: 'broadcast' | 'localstorage' | 'noop'
}

function makeBroadcastTransport(): Transport | null {
  if (typeof BroadcastChannel === 'undefined') return null
  let bc: BroadcastChannel
  try {
    bc = new BroadcastChannel(CHANNEL_NAME)
  } catch {
    return null
  }
  const listeners = new Set<Listener>()
  bc.onmessage = (ev) => {
    const msg = ev.data as SessionChannelMessage
    if (msg && typeof msg === 'object') {
      listeners.forEach((l) => l(msg))
    }
  }
  return {
    active: true,
    kind: 'broadcast',
    publish: (msg) => bc.postMessage(msg),
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

function makeLocalStorageTransport(): Transport | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null
  }
  const listeners = new Set<Listener>()
  const handler = (ev: StorageEvent) => {
    if (ev.key !== STORAGE_KEY || ev.newValue === null) return
    try {
      const msg = JSON.parse(ev.newValue) as SessionChannelMessage
      listeners.forEach((l) => l(msg))
    } catch {
      // Ignore malformed payloads.
    }
  }
  window.addEventListener('storage', handler)
  return {
    active: true,
    kind: 'localstorage',
    publish: (msg) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(msg))
      } catch {
        // Quota / private mode — silent fail.
      }
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

function makeNoopTransport(): Transport {
  return {
    active: false,
    kind: 'noop',
    publish: () => undefined,
    subscribe: () => () => undefined,
  }
}

let _transport: Transport | null = null
function getTransport(): Transport {
  if (_transport === null) {
    _transport =
      makeBroadcastTransport() ??
      makeLocalStorageTransport() ??
      makeNoopTransport()
  }
  return _transport
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

let _seq = 0

function broadcastSession(snapshot: SessionSnapshot): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'session.updated',
    ts: Date.now(),
    seq: _seq,
    session: snapshot,
  })
}

/** CFD calls this on window open to pull current session from POS.
 *  POS replies via `broadcastSession` only if it has a fully-formed
 *  session — partial states are silently ignored. */
export function requestSessionState(): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'session.request_state',
    ts: Date.now(),
    seq: _seq,
  })
}

/** POS calls this when a clerk PIN succeeds. The CFD listens and
 *  invalidates its bootstrap query so its welcome-screen logo (and any
 *  other bootstrap-derived state) reflects the freshest BE data for the
 *  current store/register/clerk. The session snapshot itself doesn't
 *  change on PIN entry (deliberately — the snapshot omits clerkToken /
 *  isAuthenticated so it can't trip route guards), so a bare `session.
 *  updated` won't fire. This is the dedicated signal for that case. */
export function broadcastBootstrapInvalidated(): void {
  _seq = (_seq + 1) | 0
  getTransport().publish({
    type: 'session.bootstrap_invalidated',
    ts: Date.now(),
    seq: _seq,
  })
}

export function onSessionChannelMessage(listener: Listener): () => void {
  return getTransport().subscribe(listener)
}

// ---------------------------------------------------------------------------
// POS-side wiring. Call once from `main.tsx`. Idempotent.
// ---------------------------------------------------------------------------

let _posWired = false

interface PosSessionInputs {
  backendUrl: string | null
  sessionToken: string | null
  selectedStore: Store | null
  selectedRegister: Register | null
  displayLogo: string | null
}

/**
 * Build a fully-formed snapshot from POS session state, or null if any of
 * the four required fields is missing. This is the publisher gate — any
 * snapshot that fails this check is treated as "POS is mid-flow" and
 * never broadcasts.
 */
function buildSnapshot(s: PosSessionInputs): SessionSnapshot | null {
  if (!s.backendUrl) return null
  if (!s.sessionToken) return null
  if (!s.selectedStore?.id) return null
  if (!s.selectedRegister?.id) return null
  return {
    backendUrl: s.backendUrl,
    sessionToken: s.sessionToken,
    selectedStore: s.selectedStore,
    selectedRegister: s.selectedRegister,
    // Do NOT gate on this — null is a valid value the CFD handles by
    // falling back to the icon.
    displayLogo: s.displayLogo,
  }
}

/**
 * Wire the POS window to broadcast session updates and reply to CFD state
 * requests. Only fires when the snapshot is fully formed AND has actually
 * changed since last broadcast.
 *
 * Idempotent — second call is a warning + no-op.
 */
export function setupPosSessionBroadcast(args: {
  getInputs: () => PosSessionInputs
  subscribe: (listener: () => void) => () => void
}): () => void {
  if (_posWired) {
    if (typeof console !== 'undefined') {
      console.warn('setupPosSessionBroadcast called twice; ignoring.')
    }
    return () => undefined
  }
  _posWired = true

  // Fingerprint of the last broadcast snapshot. Comparison is cheap (3
  // primitives) and runs once per session-store mutation, not in a hot
  // path. We never reset this back to null on logout — on the next
  // login, the new snapshot will differ and a fresh broadcast goes out.
  let lastFingerprint: string | null = null

  const broadcastIfReady = () => {
    const snapshot = buildSnapshot(args.getInputs())
    if (snapshot === null) return  // mid-flow; do not publish
    // displayLogo is part of the fingerprint so a logo upgrade (e.g.
    // clerk PIN swaps store-tier → full-hierarchy, or a /refresh picks
    // up a new BE-deployed logo) triggers a re-broadcast even when the
    // four gating fields haven't changed.
    const fp = `${snapshot.backendUrl}|${snapshot.sessionToken}|${snapshot.selectedStore.id}|${snapshot.selectedRegister.id}|${snapshot.displayLogo ?? ''}`
    if (fp === lastFingerprint) return
    lastFingerprint = fp
    broadcastSession(snapshot)
  }

  // Initial publish: if POS booted into an already-authenticated state
  // (zustand-persist rehydrated), this catches CFD up immediately. If POS
  // booted into the connect screen, buildSnapshot returns null and
  // nothing goes out — the CFD just stays on the welcome screen until
  // login completes.
  broadcastIfReady()

  const unsubStore = args.subscribe(() => {
    broadcastIfReady()
  })

  const unsubChannel = onSessionChannelMessage((msg) => {
    if (msg.type === 'session.request_state') {
      // A CFD just opened; force a republish so it gets the current
      // state even if our fingerprint cache says nothing changed.
      lastFingerprint = null
      broadcastIfReady()
    }
  })

  return () => {
    unsubStore()
    unsubChannel()
    _posWired = false
  }
}
